import React from 'react';
import styled from 'astroturf/react';

import routes from 'js/core_pre_webpack/routes';
import PremiumLock from 'js/premium/PremiumLock';
import trackClick from 'js/utils/trackClick';
import { currentUser } from 'js/utils/user';
import { ImpressionWatcher } from 'js/utils/impressions/ImpressionWatcher';

import type { PremiumUpsells } from '../../types';

const CellWatcher = styled(ImpressionWatcher)`
  display: flex;
  flex: 0 0 auto;
  height: 100%;
`;

const CellLink = styled.a`
  @import 'stylesheets/design_system/font';
  @include body__medium;

  display: flex;
  flex: 0 0 auto;
  align-items: center;
  column-gap: 8px;
  height: 100%;
  padding: 0 20px;
  background: var(--button-primary-fill, #0645b1);
  color: var(--text-white, #ffffff);
  font-weight: 600;
  text-decoration: none;
  white-space: nowrap;

  &:hover {
    background: var(--button-primary-fill-hover, #0c3b8d);
  }

  &:hover,
  &:active,
  &:visited {
    color: var(--text-white, #ffffff);
    text-decoration: none;
  }
`;

export type PremiumCtaCellProps = {
  isPremium: boolean;
  hidePremiumUpsellButton?: boolean;
  showPodcastLibraryNav?: boolean;
  premiumUpsells?: PremiumUpsells;
  bucket: string;
};

type Segment = {
  source: string;
  url: string;
  copy: string;
  extra?: object;
  controlClickSource?: string;
};

function segmentFor(premiumUpsells?: PremiumUpsells): Segment | null {
  const churnedDiscount = premiumUpsells?.churnedUserReactivationDiscount;
  if (churnedDiscount) {
    return {
      source: 'reactivate_button',
      url: routes.checkout_path({
        feature: 'churned_user_reactivation',
        trigger: 'reactivate_button',
        from_navbar: true,
      }),
      copy:
        churnedDiscount.offer === 'discount'
          ? `Reactivate Premium, ${churnedDiscount.value} OFF`
          : `Reactivate Premium for ${churnedDiscount.value}`,
      extra: { offer: churnedDiscount.offer },
      controlClickSource: 'churned_user_reactivation_button',
    };
  }

  if (premiumUpsells?.trialPricingCopy) {
    return {
      source: 'nav-btn-trial',
      url: routes.checkout_url({
        feature: 'default',
        trigger: 'nav-btn-trial',
        from_navbar: true,
      }),
      copy: premiumUpsells.trialPricingCopy,
    };
  }

  if (premiumUpsells?.reactivatePremiumUpsellType) {
    return {
      source: 'nav-btn-reactivate',
      url: routes.checkout_url({
        feature: 'default',
        trigger: 'nav-btn-reactivate',
        premium_discount_coupon: premiumUpsells.halfOffCoupon,
        from_navbar: true,
      }),
      copy: 'Reactivate Premium 50% OFF',
    };
  }

  const newUserDiscount = premiumUpsells?.newUserDiscount;
  if (newUserDiscount) {
    if (currentUser?.created_at === undefined) {
      return null;
    }
    return {
      source: newUserDiscount.trigger,
      url: routes.checkout_url({
        feature: newUserDiscount.feature,
        trigger: newUserDiscount.trigger,
        coupon_code: newUserDiscount.couponCode,
      }),
      copy: 'UPGRADE & GET 6 MONTHS FREE!',
    };
  }

  return {
    source: 'default',
    url: routes.upgrade_url({
      feature: 'default',
      trigger: 'default',
      from_navbar: true,
    }),
    copy: 'Get Premium',
  };
}

const PremiumCtaCell = ({
  isPremium,
  hidePremiumUpsellButton = false,
  showPodcastLibraryNav = false,
  premiumUpsells,
  bucket,
}: PremiumCtaCellProps) => {
  if (isPremium || hidePremiumUpsellButton || showPodcastLibraryNav) {
    return null;
  }

  const segment = segmentFor(premiumUpsells);
  if (segment == null) {
    return null;
  }

  const handleClick = () => {
    trackClick({ source: 'nav-bar--impact-premium-cta', bucket });
    if (segment.controlClickSource != null) {
      trackClick({ source: segment.controlClickSource });
    }
  };

  return (
    <CellWatcher source={segment.source} extra={segment.extra}>
      <CellLink
        href={segment.url}
        onClick={handleClick}
        data-testid="premium-cta-cell"
      >
        <PremiumLock circleColor="white" lockColor="blue" width={20} />
        {segment.copy}
      </CellLink>
    </CellWatcher>
  );
};

export default PremiumCtaCell;
